import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Play } from 'lucide-react';

const PrePublishChecks = ({ onSubmit, onNext, initialData = {} }) => {
  const [checks, setChecks] = useState(initialData.checks || {
    signoffProtocol: { status: 'pending', message: 'Sign-off protocol present' },
    auditorOpinion: { status: 'pending', message: 'Auditor\'s opinion uploaded' },
    numbersSync: { status: 'pending', message: 'Numbers sync: in sync with data_freeze_v1' },
    materialChanges: { status: 'pending', message: 'No outstanding material changes since sign-off' },
    draftWatermark: { status: 'pending', message: 'Draft watermark removed (build uses locked version)' },
    accessibility: { status: 'pending', message: 'Accessibility: PDF/UA checks (tags, alt text, reading order)' },
    legalApprovals: { status: 'pending', message: 'Legal approvals for public text' }
  });
  const [isRunning, setIsRunning] = useState(false);
  const [runCompleted, setRunCompleted] = useState(initialData.runCompleted || false);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <div className="w-5 h-5 bg-gray-200 rounded-full" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      default:
        return 'text-gray-500';
    }
  };

  const runPrePublishChecks = async () => {
    setIsRunning(true);
    
    // Simulate running checks with delays
    const checkKeys = Object.keys(checks);
    
    for (let i = 0; i < checkKeys.length; i++) {
      const key = checkKeys[i];
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
      
      // Simulate different outcomes
      let status = 'passed';
      if (key === 'accessibility') {
        status = Math.random() > 0.7 ? 'warning' : 'passed';
      } else if (key === 'legalApprovals') {
        status = Math.random() > 0.8 ? 'failed' : 'passed';
      }
      
      setChecks(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          status,
          timestamp: new Date().toISOString()
        }
      }));
    }
    
    setIsRunning(false);
    setRunCompleted(true);
  };

  const hasCriticalFailures = () => {
    return Object.values(checks).some(check => check.status === 'failed');
  };


  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Pre-publish Checks</h2>
        <p className="text-gray-600">Run automatic validation gates before publishing</p>
      </div>

      {/* Run Checks Button */}
      <div className="mb-8">
        <button
          onClick={runPrePublishChecks}
          disabled={isRunning}
          className={`flex items-center px-6 py-3 rounded-md transition-colors ${
            isRunning
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          <Play className="w-4 h-4 mr-2" />
          {isRunning ? 'Running Checks...' : 'Run Pre-publish Checks'}
        </button>
      </div>

      {/* Checks List */}
      <div className="space-y-4">
        {Object.entries(checks).map(([key, check]) => (
          <div key={key} className="flex items-center p-4 border border-gray-200 rounded-lg">
            <div className="mr-4">
              {getStatusIcon(check.status)}
            </div>
            <div className="flex-1">
              <p className={`font-medium ${getStatusColor(check.status)}`}>
                {check.message}
              </p>
              {check.timestamp && (
                <p className="text-sm text-gray-500">
                  Checked at: {new Date(check.timestamp).toLocaleTimeString()}
                </p>
              )}
            </div>
            {check.status === 'warning' && (
              <div className="text-yellow-600 text-sm">
                Manual review recommended
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      {runCompleted && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-2">Check Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-green-600">
              Passed: {Object.values(checks).filter(c => c.status === 'passed').length}
            </div>
            <div className="text-yellow-600">
              Warnings: {Object.values(checks).filter(c => c.status === 'warning').length}
            </div>
            <div className="text-red-600">
              Failed: {Object.values(checks).filter(c => c.status === 'failed').length}
            </div>
          </div>
        </div>
      )}


      {hasCriticalFailures() && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">
            Critical failures detected. Please resolve all failed checks before proceeding.
          </p>
        </div>
      )}
    </div>
  );
};

export default PrePublishChecks;
